import type { HomePage, SiteSettings, SubPage } from "./types";

/**
 * Canonical site content.
 *
 * This file has two jobs:
 *   1. Fallback when Sanity is unreachable or not yet configured.
 *   2. Seed data for Sanity — the `sanity/seed/import.ts` script ingests this.
 *
 * Every field here has a matching schema field in `sanity/schemas/*`. Editing
 * this file then rerunning `npm run seed` is how you bootstrap a fresh dataset.
 */

/* =====================================================================
 * SITE SETTINGS — nav + footer
 * ===================================================================== */

export const siteSettingsFallback: SiteSettings = {
  siteName: "Steer Health",
  tagline: "The AI workforce for healthcare operations.",
  ctaLabel: "Book a demo →",
  ctaHref: "/contact",
  nav: [
    {
      label: "Platform",
      dropdown: [
        { title: "Luna", sub: "Voice front door", href: "/luna" },
        { title: "AI Front Door", sub: "Patient access + ED", href: "/ai-front-door" },
        { title: "SteerNotes", sub: "Clinical documentation", href: "/steernotes" },
        { title: "Revenue Cycle AI", sub: "Autonomous RCM", href: "/revenue-cycle-ai" },
        { title: "SteerBrain", sub: "The reasoning layer", href: "/#brain" },
      ],
    },
    {
      label: "Segments",
      dropdown: [
        { title: "Hospital Systems", sub: "Multi-hospital networks", href: "/hospital-systems" },
        { title: "Specialty Groups", sub: "PE-backed roll-ups", href: "/specialty-groups" },
        { title: "Ambulatory Surgery Centers", sub: "Multi-OR, multi-specialty", href: "/ambulatory-surgery-centers" },
        { title: "Cash-Pay & Specialty", sub: "Functional medicine, concierge", href: "/cash-pay-specialty" },
      ],
    },
    { label: "ROI", href: "/roi" },
    {
      label: "Company",
      dropdown: [
        { title: "About us", sub: "Mission, values, team", href: "/about" },
        { title: "Customers", sub: "Production outcomes", href: "/#customers" },
        { title: "Why Steer", sub: "AI-native, agent-first", href: "/#native" },
        { title: "Contact", sub: "Let's talk", href: "/contact" },
      ],
    },
  ],
  footer: {
    tagline:
      "The AI workforce for healthcare operations. Built by operators, informed by clinicians. Founded 2021, headquartered in Irving, Texas.",
    columns: [
      {
        heading: "Platform",
        links: [
          { label: "Luna", href: "/luna" },
          { label: "AI Front Door", href: "/ai-front-door" },
          { label: "SteerNotes", href: "/steernotes" },
          { label: "Revenue Cycle AI", href: "/revenue-cycle-ai" },
          { label: "SteerBrain", href: "/#brain" },
        ],
      },
      {
        heading: "Segments",
        links: [
          { label: "Hospital Systems", href: "/hospital-systems" },
          { label: "Specialty Groups", href: "/specialty-groups" },
          { label: "ASCs", href: "/ambulatory-surgery-centers" },
          { label: "Cash-Pay Specialty", href: "/cash-pay-specialty" },
        ],
      },
      {
        heading: "Company",
        links: [
          { label: "About us", href: "/about" },
          { label: "Customers", href: "/#customers" },
          { label: "ROI", href: "/roi" },
          { label: "Contact", href: "/contact" },
        ],
      },
    ],
    bottom: [
      "© 2026 Steer Health, Inc. All rights reserved.",
      "HIPAA · SOC 2 Type II · BAA standard on every deployment",
    ],
  },
};

/* =====================================================================
 * HOME
 * ===================================================================== */

export const homeFallback: HomePage = {
  seo: {
    title: "Steer Health | The AI Workforce for Healthcare Operations",
    description:
      "The AI Workforce for Healthcare Operations. Four autonomous agents, one reasoning brain, one patient thread. Running today across 45+ hospitals and 19 million patient lives.",
    ogImage: "/assets/og-home.png",
  },
  hero: {
    eyebrow: "Live across 45+ hospital systems, 19M patient lives",
    title: "The AI Workforce<br>for <em>Healthcare<br>Operations.</em>",
    sub: "Four autonomous agents. One reasoning brain. One shared patient thread. Running today across hospital systems, specialty groups, and ambulatory surgery centers. Not a stack. Not a suite. A workforce, built agent-first from the first line of code.",
    primaryCta: { label: "Book a demo →", href: "/contact" },
    secondaryCta: { label: "See the workforce", href: "#workforce" },
  },
  proof: {
    label: "Trusted by healthcare leaders",
    logos: ["Prime Healthcare", "OrthoWest", "Saint Mary's ASC", "Forum Health", "PhyNet Dermatology"],
  },
  metrics: [
    { num: "19M", label: "Patient lives" },
    { num: "472", label: "Locations live" },
    { num: "45+", label: "Hospitals" },
    { num: "109", label: "Healthcare orgs" },
  ],
  grind: {
    eyebrow: "The problem",
    title: "Healthcare operations<br>are a <em>grind.</em>",
    sub: "The work was never rebuilt for the AI era. Every day, operators carry the same four breaks: broken front doors, exhausted clinicians, leaking revenue, and vendor fatigue yielding low to mediocre results.",
    items: [
      {
        num: "01 / The front door is broken",
        title: "Patients disappear before they ever become patients.",
        body: "Calls go unanswered. Online bookings break when they hit real scheduling rules. The ones who do reach you wait on hold long enough to Google a competitor.",
      },
      {
        num: "02 / Clinician burnout",
        title: "Charting until 10 PM isn't a medicine problem.",
        body: "It's a software problem. The systems beneath clinicians are unworkable, and every hour spent in them is an hour not spent with patients.",
      },
      {
        num: "03 / Revenue leaks everywhere",
        title: "Two to four percent of net patient revenue walks out the door.",
        body: "Denials pile up. Prior auths stall. Eligibility is checked too late to matter. The money isn't missing, it's just not getting collected.",
      },
      {
        num: "04 / Vendor fatigue",
        title: "Twelve tools to do what one platform should.",
        body: "Every integration is a new failure mode. Every renewal is a new negotiation. And the results are low to mediocre, with no one accountable for the whole.",
      },
    ],
  },
  workforce: {
    eyebrow: "The answer",
    title: "Four agents.<br>One brain. <em>One patient thread.</em>",
    sub: "Not a stack. Not a suite. A coordinated workforce of autonomous agents that share context across every interaction, built agent-first from the first line of code.",
    agents: [
      {
        name: "Luna",
        role: "Voice front door",
        desc: "Inbound and outbound at scale. New-patient booking, rescheduling, symptom-driven triage, care-gap outreach, balance recovery. Sounds like your brand. Books against your real rules.",
        stats: [
          { num: "247+", label: "Concurrent calls" },
          { num: "88%", label: "Booked without a human" },
          { num: "<400ms", label: "Response latency" },
        ],
      },
      {
        name: "AI Front Door",
        role: "Patient access + ED triage",
        desc: "One booking engine across every channel. Real-time eligibility. ESI-based triage and at-home ED registration. Patients walk in ready to be seen.",
        stats: [
          { num: "68%", label: "Self-book rate" },
          { num: "94%", label: "Eligibility verified" },
          { num: "89%", label: "Net new ED, commercial" },
        ],
      },
      {
        name: "SteerNotes",
        role: "Agentic clinical documentation",
        desc: "Codes from notes <em>and</em> the full patient record. Not just the transcript. Drafts the SOAP note, auto-codes ICD-10 and CPT, files directly to the EHR.",
        stats: [
          { num: "~1 hr", label: "Charting saved / day" },
          { num: "94%", label: "First-pass coding" },
          { num: "22+", label: "Specialties live" },
        ],
      },
      {
        name: "Revenue Cycle AI",
        role: "Autonomous RCM",
        desc: "Denials worked continuously, not weekly. Eligibility caught at booking. Patient balances recovered 24/7 through Luna. Upstream capture, not downstream cleanup.",
        stats: [
          { num: "2 to 4%", label: "Revenue recovered, Y1" },
          { num: "94%", label: "First-pass denial resolution" },
          { num: "<24 hr", label: "Denial turnaround" },
        ],
      },
    ],
    nextUp: {
      label: "Next on the workforce",
      body: "<strong>Virtual Nurse</strong> for inpatient and acute care. Because the workforce should follow the patient all the way through the hospital.",
    },
  },
  steerbrain: {
    eyebrow: "The reasoning layer",
    title: "Every agent runs on <em>SteerBrain.</em>",
    desc: "Context-rich reasoning with your rules and rails integrated. Trained on over a billion healthcare interactions. The reason our agents know your organization from day one, book against your real scheduling rules, and get smarter the longer they run.",
    features: [
      {
        title: "Your rules, integrated.",
        body: "Scheduling templates, payer logic, specialty protocols, clinical pathways. The agents work the way your organization works.",
      },
      {
        title: "Your rails, enforced.",
        body: "Safety guardrails, escalation paths, audit trails. What the agents can do, and what they route to a human, is governed end to end.",
      },
      {
        title: "One shared patient thread.",
        body: "A call with Luna informs a note with SteerNotes informs a claim with Revenue Cycle AI. Nothing falls through the cracks.",
      },
    ],
    bigNum: "1B+",
    bigLabel: "Healthcare interactions",
  },
  native: {
    eyebrow: "Why AI-native matters",
    title: "Built <em>agent-first</em><br>from the first line of code.",
    sub: "Other healthcare platforms federate acquired products at the data layer. Steer is one workforce, designed from the orchestration up, with one shared patient context. The architectural difference shows up in every deployment.",
    legacy: {
      label: "Legacy platforms",
      title: "Acquired products, stitched together.",
      points: [
        "Separate products sharing a common data store",
        "Integration seams show up in every deployment",
        "Each new capability is a new onboarding",
        "AI bolted onto software built before AI",
        "Context loss between capabilities",
      ],
    },
    steer: {
      label: "Steer",
      title: "One AI workforce, designed agent-first.",
      points: [
        "Four agents on one reasoning brain, one patient thread",
        "Shared context across every interaction, every agent",
        "Adding the next agent inherits the workforce, not integration load",
        "Built agent-first from the first line of code in 2021",
        "3 to 4 weeks from contract to production",
      ],
    },
  },
  customers: {
    eyebrow: "Proof at scale",
    title: "Production outcomes,<br>not pilot projections.",
    sub: "Steer runs in production across health systems, PE-backed specialty groups, ambulatory surgery centers, and cash-pay specialty networks. These are the real numbers our customers live with every day.",
    cards: [
      {
        name: "Prime Healthcare",
        sub: "45 hospitals · 18 months in production",
        stats: [
          { big: "45", small: "hospitals running full workforce" },
          { big: "19M", small: "patient lives on the platform" },
        ],
        quote: '"Improved volume, streamlined workflows, and timely access to care across the network."',
      },
      {
        name: "OrthoWest",
        sub: "18-provider orthopedic group",
        stats: [
          { big: "2.4x", small: "new patient visits / provider" },
          { big: "+18%", small: "gross margin, year one" },
        ],
        quote: "42% fewer inbound calls to staff. 68% of new patients self-book. Zero new FTEs per acquired site.",
      },
      {
        name: "Saint Mary's ASC",
        sub: "Multi-specialty surgical center",
        stats: [
          { big: "94%", small: "OR utilization" },
          { big: "3.8%", small: "no-show rate (from 14%)" },
        ],
        quote: "18.4 hours of staff time saved per day. 2.3 FTE-equivalents offloaded across six ORs.",
      },
    ],
  },
  ctaFinal: {
    title: "Ready to move from<br><em>grind to smooth sail?</em>",
    sub: "See the four-agent workforce and SteerBrain running on your data. Most deployments go live in three to four weeks, with outcomes measurable from week one.",
    primaryCta: { label: "Book a demo →", href: "/contact" },
    secondaryCta: { label: "Talk to an operator", href: "/contact" },
  },
};

/* =====================================================================
 * SUB PAGES (products, segments, about, contact, roi)
 *
 * Shared CTA block reused across every sub page.
 * ===================================================================== */

const commonCta = {
  title: "Hear the workforce <em>on your data.</em>",
  sub: "Most deployments go live in three to four weeks. We bring the workforce up on a sample of your real volume, real scheduling templates, and real EHR context. You see the difference on day one.",
  primaryCta: { label: "Book a demo →", href: "/contact" },
  secondaryCta: { label: "Talk to an operator", href: "/contact" },
};

const connectBlock = {
  title: "One workforce. <em>Four agents. One brain.</em>",
  body: "No single-point vendor gives you shared context across booking, documentation, and revenue cycle. Because no single-point vendor has the rest of the workforce. Each agent shares one patient thread across every interaction.",
  agents: [
    { name: "Luna", role: "Voice front door", href: "/luna" },
    { name: "AI Front Door", role: "Patient access + ED triage", href: "/ai-front-door" },
    { name: "SteerNotes", role: "Agentic clinical documentation", href: "/steernotes" },
    { name: "Revenue Cycle AI", role: "Autonomous RCM", href: "/revenue-cycle-ai" },
  ],
};

export const subPagesFallback: Record<string, SubPage> = {
  luna: {
    slug: "luna",
    seo: {
      title: "Luna | Voice front door for healthcare — Steer Health",
      description:
        "Luna is the autonomous voice agent for every healthcare conversation. Inbound and outbound, one patient context, running at production scale across 45+ hospitals.",
      ogImage: "/assets/og-luna.png",
    },
    hero: {
      eyebrow: "Luna · Voice front door",
      title: "The voice that <em>never drops a call.</em>",
      lead: "The autonomous voice agent for every healthcare conversation. Inbound and outbound. New patient booking, rescheduling, symptom triage, insurance questions, referral intake, reminders, care gap outreach, reactivation, balance recovery. One agent. One shared patient context. Running at production scale across 45+ hospitals.",
      primaryCta: { label: "Book a demo →", href: "/contact" },
      secondaryCta: { label: "See the workforce", href: "/" },
      stats: [
        { num: "247+", label: "Concurrent calls per instance" },
        { num: "<400ms", label: "Response latency" },
        { num: "88%", label: "Booked without human touch" },
        { num: "65%", label: "Of bookings after-hours" },
      ],
    },
    sections: [
      {
        _type: "capGrid",
        eyebrow: "What Luna does",
        title: "Inbound and outbound. <em>One agent.</em>",
        sub: "Most voice AI vendors handle one direction. Luna runs both, on the same patient context, with bidirectional EHR integration.",
        items: [
          {
            badge: "Inbound",
            title: "New patient booking",
            body: "First-ring answer. Collects demographics, runs eligibility in real time, books into the right visit type based on symptoms and payer, and confirms by SMS. The kind of call that used to take six minutes now takes ninety seconds.",
          },
          {
            badge: "Inbound",
            title: "Rescheduling and cancellations",
            body: "Identifies the patient, pulls the visit, offers the next available slots in the right pod, and rebooks. No staff routing, no hold time.",
          },
          {
            badge: "Inbound",
            title: "Symptom-driven triage",
            body: "Triages by urgency. Books same-day or next-day for acute symptoms, routes to nurse line for escalation, schedules routine care for everything else.",
          },
          {
            badge: "Outbound",
            title: "Reminders and reactivation",
            body: "Appointment reminders, care gap outreach for HEDIS and MIPS measures, dormant patient reactivation, post-op recovery check-ins. Every call personalized from the patient record.",
          },
          {
            badge: "Outbound",
            title: "Care gap outreach",
            body: "Surface gaps across your population (AWV, mammography, colonoscopy, A1c), then work them. Luna dials, explains the gap, and books the visit in one call.",
          },
          {
            badge: "Outbound",
            title: "Balance recovery",
            body: "24/7 polite, compliant outbound on outstanding patient balances. Payment plans, portal links, live handoff when the patient asks for a human.",
          },
        ],
      },
      {
        _type: "outcomesBand",
        eyebrow: "Production outcomes",
        title: "Running today, not in pilot.",
        sub: "Luna is live at 45+ hospital systems and 472 locations. These are portfolio averages from deployments with six or more months in production.",
        stats: [
          { num: "88%", label: "Calls booked without a human touch" },
          { num: "65%", label: "Of bookings handled after-hours" },
          { num: "42%", label: "Fewer inbound calls to staff" },
          { num: "~3 min", label: "Avg call handle time" },
        ],
      },
      {
        _type: "flow",
        eyebrow: "How it works",
        title: "From the ring to the record. <em>One agent.</em>",
        sub: "Luna is not a phone tree with a nicer voice. Every conversation is grounded in the patient's full context, the organization's real scheduling rules, and the payer's eligibility logic.",
        steps: [
          {
            num: "01",
            title: "Patient context loads",
            body: "The moment the call connects, Luna pulls the full patient record from the EHR. Past visits. Open balances. Care gaps. Preferred language and provider. Everything the staff would have asked.",
          },
          {
            num: "02",
            title: "Conversation grounded in your rules",
            body: "Luna speaks in your brand voice, books against your real templates, handles your specific payers, and follows your clinical escalation paths. Grounded at inference time, not generically trained.",
          },
          {
            num: "03",
            title: "Action written to the EHR",
            body: "Bookings, rescheduling, eligibility verification, notes: all written bidirectionally to Epic, athenahealth, eClinicalWorks, NextGen, and 40+ others. Structured data, not attachments.",
          },
          {
            num: "04",
            title: "Handoff to the workforce",
            body: "Every call updates SteerBrain. AI Front Door sees the booking. SteerNotes sees the visit context. Revenue Cycle AI sees the eligibility check. One patient thread, every agent informed.",
          },
        ],
      },
      {
        _type: "proofBox",
        data: {
          eyebrow: "Customer proof",
          title: "Prime Healthcare: 45 hospitals, 18 months live.",
          customer: "Prime Healthcare · 45 hospitals · 19M patient lives",
          quote:
            '"We have seen improved volume, revenue, streamlined workflows and expanded patient offerings to ensure timely access to care."',
          attribution: "Dr. Kavitha Bhatia, Chief Medical Officer, Prime Healthcare",
          stats: [
            { big: "45", label: "hospitals running the full workforce" },
            { big: "19M", label: "patient lives on the platform" },
          ],
        },
      },
      { _type: "connect", data: connectBlock },
    ],
    ctaFinal: {
      ...commonCta,
      title: "Hear Luna <em>on your data.</em>",
    },
  },

  "ai-front-door": {
    slug: "ai-front-door",
    seo: {
      title: "AI Front Door | Patient access + ED triage — Steer Health",
      description:
        "One booking engine across every channel. Real-time eligibility. ESI-based triage. At-home ED registration. Patients walk in ready to be seen.",
      ogImage: "/assets/og-ai-front-door.png",
    },
    hero: {
      eyebrow: "AI Front Door · Patient access + ED",
      title: "A digital front door that <em>actually closes the loop.</em>",
      lead: "One booking engine across web, phone, and SMS. Real-time eligibility at the moment of booking. ESI-based symptom triage. At-home ED registration that cuts 22 minutes out of door-to-room time. Built agent-first, not bolted onto a legacy scheduling module.",
      primaryCta: { label: "Book a demo →", href: "/contact" },
      secondaryCta: { label: "See the workforce", href: "/" },
      stats: [
        { num: "68%", label: "Self-book rate" },
        { num: "94%", label: "Eligibility verified at booking" },
        { num: "22 min", label: "Cut from ED door-to-room" },
        { num: "89%", label: "Net new ED visits, commercial payers" },
      ],
    },
    sections: [
      { _type: "connect", data: connectBlock },
    ],
    ctaFinal: { ...commonCta, title: "See the Front Door <em>on your data.</em>" },
  },

  steernotes: {
    slug: "steernotes",
    seo: {
      title: "SteerNotes | Agentic clinical documentation — Steer Health",
      description:
        "Agentic clinical documentation. Codes from the notes and the full patient record, not just the transcript. Drafts SOAP, auto-codes ICD-10/CPT, files to the EHR.",
      ogImage: "/assets/og-steernotes.png",
    },
    hero: {
      eyebrow: "SteerNotes · Agentic clinical documentation",
      title: "Documentation that <em>codes itself.</em>",
      lead: "Not ambient scribing. Agentic clinical documentation. SteerNotes drafts the SOAP note from the encounter and the full patient record, auto-codes ICD-10 and CPT at the first pass, and files structured data directly to the EHR. Saves clinicians about an hour of charting a day.",
      primaryCta: { label: "Book a demo →", href: "/contact" },
      secondaryCta: { label: "See the workforce", href: "/" },
      stats: [
        { num: "~1 hr", label: "Charting time saved / day" },
        { num: "94%", label: "First-pass coding accuracy" },
        { num: "22+", label: "Specialties in production" },
        { num: "3–4 wk", label: "From contract to live" },
      ],
    },
    sections: [
      { _type: "connect", data: connectBlock },
    ],
    ctaFinal: { ...commonCta, title: "See SteerNotes <em>on your workflows.</em>" },
  },

  "revenue-cycle-ai": {
    slug: "revenue-cycle-ai",
    seo: {
      title: "Revenue Cycle AI | Autonomous RCM — Steer Health",
      description:
        "Autonomous RCM. Denials worked continuously. Eligibility caught at booking. Patient balances recovered 24/7. Upstream capture, not downstream cleanup.",
      ogImage: "/assets/og-revenue-cycle-ai.png",
    },
    hero: {
      eyebrow: "Revenue Cycle AI · Autonomous RCM",
      title: "Revenue recovered <em>upstream, not downstream.</em>",
      lead: "Most RCM vendors chase denials after the fact. Revenue Cycle AI prevents them. Eligibility caught at booking. Prior auths worked before the visit. Denials reworked continuously, not weekly. Patient balances recovered 24/7 through Luna. Two to four percent of net patient revenue, recovered in year one.",
      primaryCta: { label: "Book a demo →", href: "/contact" },
      secondaryCta: { label: "See the workforce", href: "/" },
      stats: [
        { num: "2–4%", label: "Net revenue recovered, year one" },
        { num: "94%", label: "First-pass denial resolution" },
        { num: "<24 hr", label: "Denial turnaround" },
        { num: "24/7", label: "Balance recovery through Luna" },
      ],
    },
    sections: [
      { _type: "connect", data: connectBlock },
    ],
    ctaFinal: { ...commonCta, title: "See Revenue Cycle AI <em>on your claims.</em>" },
  },

  "hospital-systems": {
    slug: "hospital-systems",
    seo: {
      title: "Hospital Systems | Steer Health",
      description:
        "The four-agent workforce for multi-hospital networks. Deployed across Prime Healthcare's 45 hospitals and 19M patient lives.",
      ogImage: "/assets/og-hospital-systems.png",
    },
    hero: {
      eyebrow: "Segment · Hospital Systems",
      title: "A workforce for <em>every hospital in the network.</em>",
      lead: "Built for multi-hospital systems. One workforce across your access center, ED, inpatient units, and ambulatory footprint. Standardized where it helps, configurable where it matters. Live across Prime Healthcare's 45 hospitals and 19M patient lives.",
      primaryCta: { label: "Book a demo →", href: "/contact" },
      secondaryCta: { label: "See the workforce", href: "/" },
      stats: [
        { num: "45+", label: "Hospitals in production" },
        { num: "19M", label: "Patient lives on the platform" },
        { num: "472", label: "Locations live" },
        { num: "3–4 wk", label: "Per-hospital go-live" },
      ],
    },
    sections: [
      { _type: "connect", data: connectBlock },
    ],
    ctaFinal: { ...commonCta, title: "See the workforce <em>on your system.</em>" },
  },

  "specialty-groups": {
    slug: "specialty-groups",
    seo: {
      title: "Specialty Groups | Steer Health",
      description:
        "For PE-backed specialty roll-ups. New-patient growth, zero new FTEs per acquired site, operator-grade reporting across every location.",
      ogImage: "/assets/og-specialty-groups.png",
    },
    hero: {
      eyebrow: "Segment · Specialty Groups",
      title: "Grow the top line <em>without growing the back office.</em>",
      lead: "Built for PE-backed specialty roll-ups. New-patient growth across every acquired site. Zero new FTEs. Operator-grade reporting. The reason OrthoWest doubled new-patient visits per provider in year one.",
      primaryCta: { label: "Book a demo →", href: "/contact" },
      secondaryCta: { label: "See the workforce", href: "/" },
      stats: [
        { num: "2.4x", label: "New patient visits / provider" },
        { num: "+18%", label: "Gross margin, year one" },
        { num: "68%", label: "Of new patients self-book" },
        { num: "0", label: "New FTEs per acquired site" },
      ],
    },
    sections: [
      { _type: "connect", data: connectBlock },
    ],
    ctaFinal: { ...commonCta, title: "See the workforce <em>on your portfolio.</em>" },
  },

  "ambulatory-surgery-centers": {
    slug: "ambulatory-surgery-centers",
    seo: {
      title: "Ambulatory Surgery Centers | Steer Health",
      description:
        "For multi-OR, multi-specialty ASCs. OR utilization, no-show reduction, and staff time saved per day across every block.",
      ogImage: "/assets/og-asc.png",
    },
    hero: {
      eyebrow: "Segment · Ambulatory Surgery Centers",
      title: "Fill the OR. <em>Then keep it filled.</em>",
      lead: "Built for multi-OR, multi-specialty ASCs. Higher OR utilization, lower no-show rate, and real hours of staff time saved per day. Saint Mary's ASC runs the full workforce across six ORs with 94% utilization and a 3.8% no-show rate.",
      primaryCta: { label: "Book a demo →", href: "/contact" },
      secondaryCta: { label: "See the workforce", href: "/" },
      stats: [
        { num: "94%", label: "OR utilization" },
        { num: "3.8%", label: "No-show rate (from 14%)" },
        { num: "18.4 hr", label: "Staff time saved / day" },
        { num: "2.3", label: "FTE-equivalents offloaded" },
      ],
    },
    sections: [
      { _type: "connect", data: connectBlock },
    ],
    ctaFinal: { ...commonCta, title: "See the workforce <em>on your block schedule.</em>" },
  },

  "cash-pay-specialty": {
    slug: "cash-pay-specialty",
    seo: {
      title: "Cash-Pay & Specialty | Steer Health",
      description:
        "For functional medicine, concierge, med spa, and dermatology groups. Direct-to-patient booking, payment at booking, and repeat outreach.",
      ogImage: "/assets/og-cash-pay.png",
    },
    hero: {
      eyebrow: "Segment · Cash-Pay & Specialty",
      title: "Direct-to-patient <em>at the speed of e-commerce.</em>",
      lead: "Built for functional medicine, concierge, med spa, and dermatology groups. Patients book, pay, and show up, 24/7. Luna handles the follow-up. SteerNotes handles the chart. You handle the patient.",
      primaryCta: { label: "Book a demo →", href: "/contact" },
      secondaryCta: { label: "See the workforce", href: "/" },
      stats: [
        { num: "24/7", label: "Booking and payment" },
        { num: "2.8x", label: "New patient conversion" },
        { num: "<2 min", label: "Book-to-payment" },
        { num: "41%", label: "Repeat-visit lift" },
      ],
    },
    sections: [
      { _type: "connect", data: connectBlock },
    ],
    ctaFinal: { ...commonCta, title: "See the workforce <em>for your practice.</em>" },
  },

  roi: {
    slug: "roi",
    seo: {
      title: "ROI | Steer Health",
      description:
        "What a four-agent workforce is worth, on your numbers. Portfolio averages and a simple framework for sizing the impact across access, documentation, and revenue cycle.",
      ogImage: "/assets/og-roi.png",
    },
    hero: {
      eyebrow: "ROI",
      title: "What a workforce is worth, <em>on your numbers.</em>",
      lead: "Most vendor ROI is a pilot extrapolation. This is a framework built on portfolio averages from 45+ hospital systems and 19M patient lives in production. Plug in your own numbers and size the impact across access, documentation, and revenue cycle.",
      primaryCta: { label: "Book a demo →", href: "/contact" },
      secondaryCta: { label: "See the workforce", href: "/" },
      stats: [
        { num: "2–4%", label: "Net patient revenue recovered, Y1" },
        { num: "~1 hr", label: "Charting saved / clinician / day" },
        { num: "68%", label: "Self-book rate at go-live" },
        { num: "3–4 wk", label: "Contract to production" },
      ],
    },
    sections: [
      { _type: "connect", data: connectBlock },
    ],
    ctaFinal: { ...commonCta, title: "Size the workforce <em>on your numbers.</em>" },
  },

  about: {
    slug: "about",
    seo: {
      title: "About | Steer Health",
      description:
        "Built by operators, informed by clinicians. Steer Health was founded in 2021 to build the AI workforce for healthcare operations.",
      ogImage: "/assets/og-about.png",
    },
    hero: {
      eyebrow: "About Steer",
      title: "Built by operators. <em>Informed by clinicians.</em>",
      lead: "Steer Health was founded in 2021 to build the AI workforce for healthcare operations. Four autonomous agents, one reasoning brain, one shared patient thread. Headquartered in Irving, Texas. In production across 45+ hospital systems and 19M patient lives.",
      primaryCta: { label: "Book a demo →", href: "/contact" },
      secondaryCta: { label: "See the workforce", href: "/" },
      stats: [
        { num: "2021", label: "Founded" },
        { num: "109", label: "Healthcare orgs" },
        { num: "19M", label: "Patient lives" },
        { num: "Irving, TX", label: "Headquarters" },
      ],
    },
    sections: [
      {
        _type: "prose",
        eyebrow: "Why we exist",
        title: "Healthcare operations are the <em>unsolved work.</em>",
        sub: "Every health system in America is rebuilding the same back office on the same brittle software. Every specialty group is hiring the same five roles at every new site. Every ASC is losing hours to the same scheduling and intake work. We built Steer to replace that work with a workforce.",
        body: "Our founding bet in 2021: the next decade of healthcare software would not be a prettier EHR screen. It would be an autonomous workforce that sits alongside the clinicians and the staff, runs the repetitive operational work, and gives every hour back to the patient. That bet is now in production at 45+ hospital systems.",
      },
      { _type: "connect", data: connectBlock },
    ],
    ctaFinal: { ...commonCta, title: "Let's move from <em>grind to smooth sail.</em>" },
  },

  contact: {
    slug: "contact",
    seo: {
      title: "Contact | Steer Health",
      description:
        "Talk to an operator. Most deployments go live in three to four weeks. Irving, Texas. 45+ hospital systems in production.",
      ogImage: "/assets/og-contact.png",
    },
    hero: {
      eyebrow: "Contact",
      title: "Let's <em>talk.</em>",
      lead: "The fastest path is a 30-minute call with someone who has deployed Steer. Bring your current access, documentation, or RCM numbers. You'll leave with a concrete sizing on what the workforce would be worth on your data, and a timeline for a three to four week go-live.",
      primaryCta: { label: "Book a demo →", href: "mailto:hello@steerhealth.io" },
      secondaryCta: { label: "Email an operator", href: "mailto:hello@steerhealth.io" },
      stats: [
        { num: "30 min", label: "First call" },
        { num: "3–4 wk", label: "Contract to live" },
        { num: "45+", label: "Hospitals in production" },
        { num: "Irving, TX", label: "HQ" },
      ],
    },
    sections: [],
    ctaFinal: { ...commonCta, title: "Ready when you are." },
  },
};
